import { ChangeDetectionStrategy, Component, OnDestroy, effect, input, output, signal } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { LucideSearch, LucideX } from '@lucide/angular';

let nextSearchId = 0;

/** Debounced search input with a visible label and a clear button. */
@Component({
  selector: 'ds-search-field',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet],
  template: `
    <div class="ds-field">
      <label class="ds-field__label" [for]="searchId">{{ label() }}</label>
      <div class="search">
        <span class="search__icon" aria-hidden="true">
          <ng-container
            [ngComponentOutlet]="searchIcon"
            [ngComponentOutletInputs]="{ size: 16, strokeWidth: 2.25 }"
          />
        </span>
        <input
          class="ds-input search__control"
          type="search"
          [id]="searchId"
          [value]="term()"
          [placeholder]="placeholder()"
          [disabled]="disabled()"
          (input)="handleInput($event)"
          (keydown.enter)="flush()"
        />
        @if (term()) {
          <button
            type="button"
            class="search__clear"
            aria-label="Kosongkan pencarian"
            (click)="clear()"
          >
            <ng-container
              [ngComponentOutlet]="clearIcon"
              [ngComponentOutletInputs]="{ size: 16, strokeWidth: 2.5 }"
            />
          </button>
        }
      </div>
    </div>
  `,
  styles: `
    .search {
      position: relative;
      display: flex;
      align-items: center;
    }

    .search__icon {
      position: absolute;
      left: var(--space-2);
      display: inline-flex;
      pointer-events: none;
      color: var(--color-text-secondary);
    }

    .search__control {
      padding-left: var(--space-5);
      padding-right: var(--space-5);
      border: 1.5px solid var(--color-border-subtle);
      border-radius: var(--radius-md);

      &:hover:not(:disabled) {
        border-color: var(--color-primary);
      }

      &:focus-visible {
        outline: 2px solid var(--color-primary);
        outline-offset: 2px;
      }

      &::-webkit-search-cancel-button {
        display: none;
      }
    }

    .search__clear {
      position: absolute;
      right: var(--space-2);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border: 0;
      border-radius: var(--radius-full);
      background: transparent;
      color: var(--color-text-secondary);
      cursor: pointer;
      transition: background-color var(--motion-fast);

      &:hover {
        background: var(--color-bg-subtle);
        color: var(--color-text);
      }
    }
  `,
})
export class SearchFieldComponent implements OnDestroy {
  label = input('Cari');
  placeholder = input('Ketik kata kunci…');
  disabled = input(false);
  debounceMs = input(400);
  value = input('');

  constructor() {
    effect(() => this.term.set(this.value()));
  }

  /** Emits the trimmed term after the debounce delay (or immediately on Enter/clear). */
  searchTerm = output<string>();

  protected readonly searchId = `ds-search-${nextSearchId++}`;
  protected readonly term = signal('');
  protected readonly searchIcon = LucideSearch;
  protected readonly clearIcon = LucideX;

  private timer: ReturnType<typeof setTimeout> | undefined;

  protected handleInput(event: Event): void {
    this.term.set((event.target as HTMLInputElement).value);
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.searchTerm.emit(this.term().trim()), this.debounceMs());
  }

  protected flush(): void {
    clearTimeout(this.timer);
    this.searchTerm.emit(this.term().trim());
  }

  protected clear(): void {
    clearTimeout(this.timer);
    this.term.set('');
    this.searchTerm.emit('');
  }

  ngOnDestroy(): void {
    clearTimeout(this.timer);
  }
}
